import React from "react";
import color from "../../resource/color";
import Text from "../Text/Text";
import { Space } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { numberFormatter } from "../../utility/Formatter";
import { useProgressAnimation } from "../../hook/useProgressAnimation";

type Milestone = {
  value: number;
  icon?: string;
  name: string;
  targetAmount: string;
  rewardNum: number;
  isSpecial: boolean;
};

type Props = {
  progress: number;
  milestones: Milestone[];
};

const PointProgressBar: React.FC<Props> = ({ progress, milestones }) => {
  const normalMilestones = milestones.filter((m) => !m.isSpecial);
  const normalWidth = (normalMilestones.length / milestones.length) * 100;
  const cappedProgress = Math.min(progress, 100);
  const normalProgress = Math.min(cappedProgress, normalWidth);
  const specialProgress = Math.max(cappedProgress - normalWidth, 0);

  const { normalWidthState, specialWidth, showSpecial } = useProgressAnimation(
    normalProgress,
    specialProgress,
  );

  const getCurrentProgress = (m: Milestone) =>
    m.isSpecial ? normalWidthState + specialWidth : normalWidthState;

  return (
    <div style={{ position: "relative", padding: "20px 40px 0px 0px" }}>
      <style>
        {`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>

      <div
        style={{
          position: "relative",
          height: "140px",
          width: "100%",
          maxWidth: "1000px",
          margin: "0 auto",
        }}
      >
        {/* Background bar */}
        <div
          style={{
            backgroundColor: "#e0e0e0",
            width: "100%",
            height: "9px",
            borderRadius: "6px",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />

        {/* Normal progress */}
        <div
          style={{
            background: `linear-gradient(270deg, ${color.primary}, ${color.softPrimary}, ${color.primary})`,
            backgroundSize: "200% 200%",
            width: `${normalWidthState}%`,
            height: "9px",
            borderRadius: !showSpecial ? "6px" : "6px 0 0 6px",
            position: "absolute",
            top: "50%",
            left: 0,
            transform: "translateY(-50%)",
            transition: "width 0.2s linear",
            animation: "gradientMove 3s ease infinite",
          }}
        />

        {/* Special progress */}
        {showSpecial && (
          <div
            style={{
              background: `linear-gradient(270deg, ${color.warning}, ${color.hardWarning}, ${color.error})`,
              backgroundSize: "200% 200%",
              width: `${specialWidth}%`,
              height: "9px",
              borderRadius: "0 6px 6px 0",
              position: "absolute",
              top: "50%",
              left: `${normalWidth}%`,
              transform: "translateY(-50%)",
              transition: "width 0.2s linear",
              animation: "gradientMove 3s ease infinite",
            }}
          />
        )}

        {milestones.map((m, index) => {
          const milestonePosition = ((index + 1) / milestones.length) * 100;
          const isAchieved = getCurrentProgress(m) >= milestonePosition;

          return (
            <div
              key={index}
              style={{
                width: "max-content",
                position: "absolute",
                top: "42%",
                transform: "translateX(-50%)",
                textAlign: "center",
                left: `calc(${milestonePosition}%)`,
                transition: "opacity 0.5s ease-in-out",
              }}
            >
              <div
                style={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  backgroundColor: isAchieved
                    ? m.isSpecial
                      ? color.warning
                      : color.primary
                    : "#ccc",
                  border: "2px solid white",
                  margin: "0 auto 6px auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isAchieved ? "0 2px 6px rgba(0,0,0,0.4)" : "none",
                }}
              >
                {isAchieved && <CheckCircleOutlined style={{ fontSize: 22, color: color.white }} />}
              </div>

              <div
                style={{
                  position: "absolute",
                  top: "-80px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "max-content",
                }}
              >
                <Space wrap size={0} direction='vertical' style={{ alignItems: "center" }}>
                  {m.icon && (
                    <img
                      src={m.icon}
                      style={{
                        width: "clamp(42px, 8vw, 44px)",
                        height: "clamp(38px, 6vw, 38px)",
                      }}
                    />
                  )}
                  <Text fontFamily='Sarabun' fontSize={12} fontWeight={400} color='background4'>
                    {m.isSpecial ? `รางวัลพิเศษที่ ${m.rewardNum}` : `ระดับที่ ${m.rewardNum}`}
                  </Text>
                  <Text fontFamily='Sarabun' fontSize={14} fontWeight={600}>
                    {m.name}
                  </Text>
                </Space>
              </div>
              <div style={{ display: "flex", width: "120px" }}>
                <Text fontFamily='Sarabun' fontSize={12} fontWeight={600}>
                  {numberFormatter(Number(m.targetAmount), 2)} บาท
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PointProgressBar;
